import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { api } from '@verifime/api-definition';
import { TApiScanIdentityDocumentResponse } from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { usePolling } from '@verifime/utils';
import { useEffect, useRef, useState } from 'react';
import { useFileUploaderContext } from './FileUploaderContext';
import { isFormDataEmpty, useFormContext } from './FormContext';
import { usePhotoGuideContext } from './PhotoGuideContext';
import { getSavedScanId } from './savedScanId';
import { useScanContext } from './ScanContext';
import { useScrollTargetContext } from './ScrollTargetContext';
import useScanResultGate from './useScanResultGate';
import { buildOcrData } from './utils';
import { useWizardContext } from './WizardContext';

const LoadingIndicator = () => {
  return (
    <Stack direction="row" gap={tokens.spacingBase}>
      <CircularProgress color="inherit" size={20} />
      <Typography>Extracting information from your ID</Typography>
    </Stack>
  );
};

type TScanIdentityDocumentProps = Readonly<{
  currentStep: number;
}>;

export default function ScanIdentityDocumentView({ currentStep }: TScanIdentityDocumentProps) {
  const [isScanning, setIsScanning] = useState(false);
  // The scan whose body this mount handled last, for each document type (the mount is kept when the
  // type changes). A body from another scan is a newer scan and replaces what the form holds, for
  // example a photo added while the first scan was running.
  const lastBodyScanIdsRef = useRef<Record<string, string | null>>({});

  const {
    state: {
      customerId,
      uploadIdDocumentConfig: { pollingIntervalInMs, maxRetry },
    },
    getSelectedDocumentTypeForStep,
  } = useWizardContext();
  const {
    state: { scanFilesId },
  } = useFileUploaderContext();
  const {
    state: { scanMutationId, scanStatus },
    scanFailed,
    scanSuccessed,
    startScan,
  } = useScanContext();
  const {
    state: { formData },
    setFormData,
    setFormDataIfEmpty,
  } = useFormContext();
  const {
    state: { fileIds },
  } = useFileUploaderContext();

  const { setIsShowCompactPhotoGuidance } = usePhotoGuideContext();

  const { scrollToTarget } = useScrollTargetContext();

  const selectedDocumentType = getSelectedDocumentTypeForStep(currentStep);

  const scanResultGate = useScanResultGate();

  const handleScanSucceeded = (data: TApiScanIdentityDocumentResponse) => {
    scanSuccessed();
    stopPolling();
    setIsScanning(false);
    // A scan body never replaces values the form already holds (saved earlier, typed, or from
    // this scan), unless it is from a newer scan than the one those values belong to: the scan
    // handled before in this mount, or else the one the saved value was saved with. A step that
    // mounts again polls a scan that already succeeded (verifime-adr#791). `scanFilesId` is the
    // scan this render's poll was sent for, so a late body carries its own id.
    const knownScanId =
      lastBodyScanIdsRef.current[selectedDocumentType] ??
      getSavedScanId(customerId, selectedDocumentType);
    lastBodyScanIdsRef.current[selectedDocumentType] = scanFilesId;
    const ocrData = buildOcrData(selectedDocumentType, data);
    if (knownScanId !== null && scanFilesId !== knownScanId) {
      setFormData(ocrData);
    } else {
      setFormDataIfEmpty(ocrData);
    }
  };
  const handleScanFailed = () => {
    setIsScanning(false);
    stopPolling();
    scanFailed();
    setIsShowCompactPhotoGuidance(false);
    setFormData(null);
    // Scroll to photo guidance after state updates
    scrollToTarget('photo-guide');
  };

  const { startPolling, stopPolling } = usePolling({
    autoStart: false,
    pollingAction: () => {
      if (!scanFilesId) {
        stopPolling();
        setIsScanning(false);
        return;
      }
      if (selectedDocumentType === 'OtherIdentityDocument') {
        stopPolling();
        setIsScanning(false);
        return;
      }
      setIsScanning(true);
      const scanTicket = scanResultGate.takeTicket();
      return api
        .getV2personIdidentityDocumentTypescanResultIdentityDocumentScanId({
          params: {
            id: customerId,
            type: selectedDocumentType,
            identityDocumentScanId: scanFilesId,
          },
        })
        .then((data) => {
          if (data && scanResultGate.acceptBody(scanTicket)) {
            handleScanSucceeded(data);
          }
        })
        .catch((error) => {
          console.error('Error scanning identity document: ', error);
          if (scanResultGate.isLive(scanTicket)) {
            handleScanFailed();
          }
        });
    },
    pollingIntervalInMs,
    maxRetry,
    onMaxRetryReached: () => {
      handleScanFailed();
    },
  });

  useEffect(() => {
    const hasFiles = (fileIds?.length || 0) > 0;
    const hasNoFormData = isFormDataEmpty(formData);
    if (scanStatus === 'BYPASSED') {
      stopPolling();
      setIsScanning(false);
      return;
    }
    if (hasFiles && hasNoFormData && scanStatus === null) {
      startScan();
    }
    return () => {
      stopPolling();
      setIsScanning(false);
    };
  }, [formData, fileIds, scanStatus]);

  useEffect(() => {
    if (scanMutationId) {
      if (scanMutationId.action === 'CANCEL') {
        stopPolling();
        setIsScanning(false);
        return;
      }
      if (scanMutationId.action === 'SCAN') {
        scanResultGate.beginScan();
        startPolling();
      }
    }
    return () => {
      scanResultGate.endScan();
      stopPolling();
    };
  }, [scanMutationId, startPolling, stopPolling, scanResultGate]);

  if (isScanning) {
    return (
      <Box data-component="ScanIdentityDocument" sx={{ width: '100%' }}>
        <LoadingIndicator />
      </Box>
    );
  }
  return null;
}
